/**
 * Katalog-Reiter der Einstellungen (Aufgabe 12): Übersicht und Aufräumwerkzeug
 * für den Softwarekatalog, der laut Spec E9 beim Tippen in der Kundenakte
 * entsteht — hier NICHT der Weg, auf dem Daten hineinkommen, sondern die
 * Stelle, an der man sie pflegt und bereinigt (Vorlaufwerte, Wartungsenden).
 *
 * Muster: FrequenciesSettings (settings.tsx) für inline-editierbare Zahlenfelder,
 * ExpiringTable (wartung.tsx) für die reine Anzeige.
 */
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../../lib/api.js";
import { flacheHierarchie } from "./katalogBaum.js";
import {
  formatMaintenanceEnd,
  fromLocalDateString,
  toLocalDateString,
} from "../../lib/date.js";
import {
  useCreateProduct,
  useMergeProducts,
  useMoveVersions,
  useProducts,
  useUpdateProductThresholds,
  useUpsertLifecycle,
  useVersions,
  type MaintenanceThresholdDefaults,
  type ProductRef,
  type VersionRef,
} from "../../hooks/useMaintenance.js";

const CATALOG_COLUMN_COUNT = 10;

export function SoftwareCatalogTab() {
  const [createOpen, setCreateOpen] = useState(false);
  /**
   * Tabelle oder Baum. Beides, nicht entweder-oder: Die Tabelle ist die
   * Arbeitsflaeche (pflegen, auswaehlen, verschieben), der Baum beantwortet
   * „wie haengt das zusammen" — was man in einer Spalte „Linie" erst sieht,
   * wenn man sie im Kopf sortiert.
   */
  const [ansicht, setAnsicht] = useState<"tabelle" | "baum">("tabelle");
  const { t } = useTranslation();
  const { data, isLoading, isError } = useProducts();
  const [onlyNew, setOnlyNew] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [mergeSource, setMergeSource] = useState<ProductRef | null>(null);

  const rows = (data?.items ?? []).filter((p) => !onlyNew || !p.hasLifecycle);

  function toggleExpanded(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Text c="dimmed" size="sm">
          {t("pages.settings.catalog.subtitle")}
        </Text>
        <Group gap="sm">
          {/*
            Anlegen gehoert hierher, seit das Verschieben hier sitzt: Es
            braucht zwingend ein Zielprodukt. Ohne diesen Knopf musste man den
            Katalog verlassen, eine fremde Kundenakte anfassen und dort eine
            Dummy-Version erfinden, weil das Feld dort Pflicht ist (Befund B1
            aus dem Staging-Test vom 2026-08-19).
          */}
          <SegmentedControl
            size="xs"
            value={ansicht}
            onChange={(v: string) => setAnsicht(v as "tabelle" | "baum")}
            data={[
              { value: "tabelle", label: t("pages.settings.catalog.viewTable") },
              { value: "baum", label: t("pages.settings.catalog.viewTree") },
            ]}
          />
          <Button size="xs" variant="light" onClick={() => setCreateOpen(true)}>
            {t("pages.settings.catalog.createAction")}
          </Button>
          <Checkbox
            label={t("pages.settings.catalog.onlyNewFilter")}
            checked={onlyNew}
            onChange={(e) => setOnlyNew(e.currentTarget.checked)}
          />
        </Group>
      </Group>

      <CreateProductModal opened={createOpen} onClose={() => setCreateOpen(false)} />

      <Paper p="md" withBorder>
        {isError ? (
          <Alert color="red" title={t("common.error")}>
            {t("common.errorLoading")}
          </Alert>
        ) : isLoading || !data ? (
          <Skeleton height={240} />
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 32 }} />
                <Table.Th>{t("pages.settings.catalog.tableName")}</Table.Th>
                <Table.Th>{t("pages.settings.catalog.tableVendor")}</Table.Th>
                <Table.Th style={{ width: 150 }}>{t("pages.settings.catalog.tableOrange")}</Table.Th>
                <Table.Th style={{ width: 150 }}>{t("pages.settings.catalog.tableRed")}</Table.Th>
                <Table.Th style={{ width: 100 }} />
                <Table.Th style={{ width: 90 }}>{t("pages.settings.catalog.tableVersions")}</Table.Th>
                <Table.Th style={{ width: 90 }}>{t("pages.settings.catalog.tableSystems")}</Table.Th>
                <Table.Th style={{ width: 140 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((p) => (
                <ProductRow
                  key={p.id}
                  product={p}
                  defaults={data.defaults}
                  expanded={expandedIds.has(p.id)}
                  onToggle={() => toggleExpanded(p.id)}
                  onMerge={() => setMergeSource(p)}
                  // Alle Produkte, nicht nur die gefilterten Zeilen: Das Ziel
                  // eines Verschiebens kann durchaus ausgeblendet sein.
                  allProducts={data.items}
                  hierarchisch={ansicht === "baum"}
                />
              ))}
              {rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={CATALOG_COLUMN_COUNT}>
                    <Text c="dimmed" ta="center" py="md">
                      {t(onlyNew ? "pages.settings.catalog.emptyNew" : "pages.settings.catalog.empty")}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <MergeProductModal
        source={mergeSource}
        products={data?.items ?? []}
        onClose={() => setMergeSource(null)}
      />
    </Stack>
  );
}

// ── Produktzeile ─────────────────────────────────────────────────────────────

function ProductRow({
  product,
  defaults,
  expanded,
  onToggle,
  onMerge,
  allProducts,
  hierarchisch,
}: {
  product: ProductRef;
  defaults: MaintenanceThresholdDefaults;
  expanded: boolean;
  onToggle: () => void;
  onMerge: () => void;
  /** Alle Produkte — Ziele fuers Verschieben von Versionen. */
  allProducts: ProductRef[];
  /** Versionen hierarchisch sortiert und eingerueckt statt flach. */
  hierarchisch: boolean;
}) {
  const { t } = useTranslation();
  const updateThresholds = useUpdateProductThresholds();
  const { data: versions, isLoading: versionsLoading } = useVersions(expanded ? product.id : null);

  const [orange, setOrange] = useState<number | string>(product.warnOrangeMonths ?? "");
  const [red, setRed] = useState<number | string>(product.warnRedMonths ?? "");
  // Stammdaten: seit dem 2026-08-19 aenderbar. Vorher liessen sich Anbieter
  // und Schema nur bei der Anlage setzen — und der Wartungs-Import setzt den
  // Anbieter nie, im Katalog stand deshalb ueberall ein Gedankenstrich.
  const [name, setName] = useState(product.name);
  const [vendor, setVendor] = useState(product.vendor ?? "");

  useEffect(() => {
    setOrange(product.warnOrangeMonths ?? "");
    setRed(product.warnRedMonths ?? "");
    setName(product.name);
    setVendor(product.vendor ?? "");
  }, [
    product.warnOrangeMonths,
    product.warnRedMonths,
    product.name,
    product.vendor,
    product.id,
  ]);

  const orangeNum = typeof orange === "number" ? orange : orange === "" ? null : parseFloat(orange);
  const redNum = typeof red === "number" ? red : red === "" ? null : parseFloat(red);
  const dirty =
    (orangeNum ?? null) !== (product.warnOrangeMonths ?? null) ||
    (redNum ?? null) !== (product.warnRedMonths ?? null) ||
    name.trim() !== product.name ||
    vendor.trim() !== (product.vendor ?? "");

  function handleSaveThresholds() {
    updateThresholds.mutate(
      {
      id: product.id,
      // Leerer Anbieter heisst „keiner" — als null, nicht als Leerstring,
      // sonst steht spaeter ein unsichtbarer Wert in der Spalte.
      name: name.trim() || product.name,
      vendor: vendor.trim() === "" ? null : vendor.trim(),
      warnOrangeMonths: orangeNum !== null && !Number.isNaN(orangeNum) ? orangeNum : null,
        warnRedMonths: redNum !== null && !Number.isNaN(redNum) ? redNum : null,
      },
      {
        // Ohne Quittung erkennt man eine gespeicherte Aenderung nur daran,
        // dass der Knopf wieder grau wird (Befund B4).
        onSuccess: () =>
          notifications.show({
            message: t("pages.settings.catalog.savedToast", { name: name.trim() }),
            color: "green",
            autoClose: 3000,
            withBorder: true,
          }),
      },
    );
  }

  return (
    <>
      <Table.Tr>
        <Table.Td>
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            onClick={onToggle}
            aria-label={
              expanded
                ? t("pages.settings.catalog.collapseAria")
                : t("pages.settings.catalog.expandAria")
            }
          >
            {expanded ? "▾" : "▸"}
          </Button>
        </Table.Td>
        <Table.Td>
          <TextInput
            size="xs"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            aria-label={t("pages.settings.catalog.tableName")}
            style={{ minWidth: 180 }}
          />
        </Table.Td>
        <Table.Td>
          <TextInput
            size="xs"
            value={vendor}
            onChange={(e) => setVendor(e.currentTarget.value)}
            placeholder={t("common.dashed")}
            aria-label={t("pages.settings.catalog.tableVendor")}
            style={{ minWidth: 120 }}
          />
        </Table.Td>

        <Table.Td>
          <Stack gap={2}>
            <NumberInput
              size="xs"
              value={orange}
              onChange={setOrange}
              min={0}
              max={120}
              allowDecimal={false}
              placeholder={t("pages.settings.catalog.globalPlaceholder")}
            />
            {orange === "" && (
              <Text size="xs" c="dimmed">
                {t("pages.settings.catalog.globalValue", { months: defaults.warnOrangeMonths })}
              </Text>
            )}
          </Stack>
        </Table.Td>
        <Table.Td>
          <Stack gap={2}>
            <NumberInput
              size="xs"
              value={red}
              onChange={setRed}
              min={0}
              max={120}
              allowDecimal={false}
              placeholder={t("pages.settings.catalog.globalPlaceholder")}
            />
            {red === "" && (
              <Text size="xs" c="dimmed">
                {t("pages.settings.catalog.globalValue", { months: defaults.warnRedMonths })}
              </Text>
            )}
          </Stack>
        </Table.Td>
        <Table.Td>
          <Button
            size="xs"
            variant="light"
            disabled={!dirty}
            loading={updateThresholds.isPending}
            onClick={handleSaveThresholds}
          >
            {t("common.save")}
          </Button>
        </Table.Td>
        <Table.Td>{product.versionCount}</Table.Td>
        <Table.Td>{product.systemCount}</Table.Td>
        <Table.Td>
          <Button size="xs" variant="default" onClick={onMerge}>
            {t("pages.settings.catalog.mergeAction")}
          </Button>
        </Table.Td>
      </Table.Tr>
      {updateThresholds.isError && (
        <Table.Tr>
          <Table.Td colSpan={CATALOG_COLUMN_COUNT}>
            {/* Die Meldung des Servers durchreichen statt sie durch ein
                pauschales „Speichern fehlgeschlagen" zu ersetzen: Bei der
                Schwellenprüfung (Orange muss vor Rot warnen) steht in der
                Serverantwort genau, welche zwei Zahlen sich widersprechen —
                ohne sie wüsste der Anwender nur, DASS etwas nicht ging. */}
            <Text size="xs" c="red">
              {updateThresholds.error instanceof ApiError
                ? updateThresholds.error.message
                : t("maintenance.card.saveError")}
            </Text>
          </Table.Td>
        </Table.Tr>
      )}
      {expanded && (
        <Table.Tr>
          <Table.Td colSpan={CATALOG_COLUMN_COUNT} style={{ background: "var(--mantine-color-default-hover)" }}>
            {versionsLoading ? (
              <Skeleton height={100} />
            ) : (
              <VersionsTable
                versions={versions ?? []}
                productId={product.id}
                products={allProducts}
                hierarchisch={hierarchisch}
              />
            )}
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

// ── Versionen je Produkt ─────────────────────────────────────────────────────

function VersionsTable({
  versions,
  productId,
  products,
  hierarchisch,
}: {
  versions: VersionRef[];
  productId: string;
  products: ProductRef[];
  /**
   * Hierarchisch: Linie, darunter eingerueckt ihre Staende — statt der flachen
   * Liste mit einer Spalte „Linie". Es ist DIESELBE Tabelle mit denselben
   * Aktionen; nur Reihenfolge und Einrueckung aendern sich. Zwei getrennte
   * Ansichten waeren zwei Codepfade, und die zweite koennte weniger.
   */
  hierarchisch: boolean;
}) {
  const { t } = useTranslation();
  const [editTarget, setEditTarget] = useState<VersionRef | null>(null);
  /**
   * Auswahl fuers Verschieben in ein anderes Produkt.
   *
   * Nachfahren nimmt der Server von selbst dazu — die Oberflaeche muss also
   * nicht mitdenken, wenn jemand nur die Linie ankreuzt. Umgekehrt lehnt er
   * ab, wenn eine Version ohne ihre Linie ginge; die Meldung nennt die Faelle
   * und wird unveraendert angezeigt.
   */
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  const [zielProdukt, setZielProdukt] = useState<string | null>(null);
  const moveVersions = useMoveVersions();

  const andereProdukte = products.filter((p) => p.id !== productId);

  return (
    <>
      {gewaehlt.length > 0 && (
        <Group
          gap="sm"
          align="center"
          mb="xs"
          p="xs"
          style={{
            background: "var(--mantine-color-body)",
            border: "1px solid var(--mantine-color-default-border)",
            borderRadius: 4,
          }}
        >
          <Text size="xs" fw={500}>
            {t("pages.settings.catalog.moveSelected", { count: gewaehlt.length })}
          </Text>
          <Select
            size="xs"
            placeholder={t("pages.settings.catalog.moveTarget")}
            data={andereProdukte.map((p) => ({ value: p.id, label: p.name }))}
            value={zielProdukt}
            onChange={setZielProdukt}
            searchable
            style={{ minWidth: 220 }}
          />
          <Button
            size="xs"
            disabled={!zielProdukt}
            loading={moveVersions.isPending}
            onClick={() =>
              zielProdukt &&
              moveVersions.mutate(
                {
                  fromProductId: productId,
                  toProductId: zielProdukt,
                  versionIds: gewaehlt,
                },
                {
                  onSuccess: (res) => {
                    setGewaehlt([]);
                    setZielProdukt(null);
                    // Gerade bei einer Massenaktion ist die Zahl die
                    // eigentliche Quittung: Sie sagt auch, dass die Nachfahren
                    // mitgegangen sind (Befund B4).
                    notifications.show({
                      message: t("pages.settings.catalog.movedToast", {
                        versions: res.movedVersions,
                        systems: res.movedStates,
                      }),
                      color: "green",
                      autoClose: 5000,
                      withBorder: true,
                    });
                  },
                },
              )
            }
          >
            {t("pages.settings.catalog.moveAction")}
          </Button>
          <Button size="xs" variant="subtle" onClick={() => setGewaehlt([])}>
            {t("common.cancel")}
          </Button>
          {moveVersions.isError && (
            <Text size="xs" c="red">
              {moveVersions.error instanceof ApiError
                ? moveVersions.error.message
                : t("maintenance.card.saveError")}
            </Text>
          )}
        </Group>
      )}
      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th style={{ width: 36 }} />
            <Table.Th>{t("pages.wartung.tableVersion")}</Table.Th>
            {/* Die Spalte „Linie" ist in der Baumform redundant — dort zeigt
                die Einrueckung, was hier als Wert danebensteht. */}
            {!hierarchisch && (
              <Table.Th>{t("pages.settings.catalog.versionLine")}</Table.Th>
            )}
            <Table.Th>{t("pages.wartung.tableMaintenanceEnd")}</Table.Th>
            <Table.Th>{t("pages.settings.catalog.versionOrigin")}</Table.Th>
            <Table.Th>{t("pages.settings.catalog.versionSource")}</Table.Th>
            <Table.Th style={{ width: 90 }}>{t("pages.settings.catalog.tableSystems")}</Table.Th>
            <Table.Th style={{ width: 100 }} />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(hierarchisch
            ? flacheHierarchie(versions)
            : versions.map((version) => ({ version, tiefe: 0 }))
          ).map(({ version: v, tiefe }) => (
            <Table.Tr key={v.id}>
              <Table.Td>
                <Checkbox
                  size="xs"
                  checked={gewaehlt.includes(v.id)}
                  onChange={(e) =>
                    setGewaehlt((bisher) =>
                      e.currentTarget.checked
                        ? [...bisher, v.id]
                        : bisher.filter((id) => id !== v.id),
                    )
                  }
                  aria-label={v.label}
                />
              </Table.Td>
              <Table.Td>
                <Text
                  size="sm"
                  pl={tiefe * 24}
                  fw={hierarchisch && tiefe === 0 ? 500 : 400}
                >
                  {tiefe > 0 && (
                    <Text span c="dimmed" mr={6}>
                      └
                    </Text>
                  )}
                  {v.label}
                </Text>
              </Table.Td>
              {!hierarchisch && (
                <Table.Td>
                  {v.parentVersionLabel ?? t("pages.settings.catalog.versionIsLine")}
                </Table.Td>
              )}
              <Table.Td>{formatMaintenanceEnd(v.maintenanceEnd, v.granularity)}</Table.Td>
              <Table.Td>
                {v.maintenanceEnd === null ? (
                  t("common.dashed")
                ) : (
                  <Badge color={v.inheritedFromLine ? "gray" : "blue"} variant="light">
                    {v.inheritedFromLine
                      ? t("pages.settings.catalog.originInherited")
                      : t("pages.settings.catalog.originOwn")}
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>{v.source ?? t("common.dashed")}</Table.Td>
              <Table.Td>{v.systemCount}</Table.Td>
              <Table.Td>
                <Button size="xs" variant="default" onClick={() => setEditTarget(v)}>
                  {t("common.edit")}
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
          {versions.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={hierarchisch ? 7 : 8}>
                <Text c="dimmed" ta="center" py="sm">
                  {t("pages.settings.catalog.versionsEmpty")}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <LifecycleEditModal target={editTarget} onClose={() => setEditTarget(null)} />
    </>
  );
}

function LifecycleEditModal({
  target,
  onClose,
}: {
  target: VersionRef | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const upsertLifecycle = useUpsertLifecycle();

  // Befund U5: Das Wartungsende steckt hier als `Date` (lokale Mitternacht)
  // statt als ISO-String — dieselbe Währung wie in der Kundenakte, damit
  // beide Stellen dasselbe Steuerelement benutzen können. Serialisiert wird
  // beim Speichern über `toLocalDateString`; ISO bleibt das Format auf der
  // Leitung und in der Datenbank.
  const [maintenanceEnd, setMaintenanceEnd] = useState<Date | null>(null);
  const [granularity, setGranularity] = useState<"tag" | "quartal">("tag");
  const [source, setSource] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setMaintenanceEnd(target?.maintenanceEnd ? fromLocalDateString(target.maintenanceEnd) : null);
    setGranularity(target?.granularity ?? "tag");
    setSource(target?.source ?? "");
    setError(null);
  }, [target]);

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    if (!target) return;
    if (!maintenanceEnd) {
      setError(t("pages.settings.catalog.versionEditDateRequired"));
      return;
    }
    try {
      await upsertLifecycle.mutateAsync({
        versionId: target.id,
        maintenanceEnd: toLocalDateString(maintenanceEnd),
        granularity,
        source: source.trim() || undefined,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("maintenance.card.saveError"));
    }
  }

  return (
    <Modal
      opened={target !== null}
      onClose={handleClose}
      title={
        target
          ? t("pages.settings.catalog.versionEditTitle", { label: target.label })
          : ""
      }
      size="sm"
      centered
    >
      <Stack gap="sm">
        {/* Befund U5: war ein natives `type="date"`-Feld und damit das
            einzige Datumsfeld der Wartungsplanung mit eigener Bedienung. */}
        <DateInput
          label={t("pages.wartung.tableMaintenanceEnd")}
          placeholder={t("common.datePlaceholder")}
          value={maintenanceEnd}
          onChange={setMaintenanceEnd}
          required
        />
        <Select
          label={t("pages.settings.catalog.versionGranularity")}
          data={[
            { value: "tag", label: t("pages.settings.catalog.granularityDay") },
            { value: "quartal", label: t("pages.settings.catalog.granularityQuarter") },
          ]}
          value={granularity}
          onChange={(v) => v && setGranularity(v as "tag" | "quartal")}
          allowDeselect={false}
        />
        <TextInput
          label={t("pages.settings.catalog.versionSource")}
          value={source}
          onChange={(e) => setSource(e.currentTarget.value)}
          placeholder={t("pages.settings.catalog.versionSourcePlaceholder")}
        />
        {error && (
          <Alert color="red" title={t("common.error")}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={upsertLifecycle.isPending}>
            {t("common.cancel")}
          </Button>
          <Button loading={upsertLifecycle.isPending} onClick={() => void handleSubmit()}>
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Zusammenführen ───────────────────────────────────────────────────────────

/**
 * Dubletten zusammenführen (Aufgabe 15, Spec §8): Löschen ist über die
 * `onDelete: "restrict"`-Fremdschlüssel gar nicht möglich — das ist das
 * einzige Aufräumwerkzeug. Die Rückfrage nennt die Zahl der betroffenen
 * Systeme (`source.systemCount`, bereits über `GET /maintenance/products`
 * geladen) statt eines separaten Vorschau-Aufrufs.
 */
function MergeProductModal({
  source,
  products,
  onClose,
}: {
  source: ProductRef | null;
  products: ProductRef[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const mergeProducts = useMergeProducts();

  const [targetId, setTargetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTargetId(null);
    setError(null);
  }, [source]);

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleSubmit() {
    if (!source || !targetId) return;
    try {
      await mergeProducts.mutateAsync({ fromId: source.id, intoId: targetId });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("maintenance.card.saveError"));
    }
  }

  const options = products
    .filter((p) => p.id !== source?.id)
    .map((p) => ({ value: p.id, label: p.name }));

  return (
    <Modal
      opened={source !== null}
      onClose={handleClose}
      title={source ? t("pages.settings.catalog.mergeTitle", { name: source.name }) : ""}
      size="sm"
      centered
    >
      <Stack gap="sm">
        <Select
          label={t("pages.settings.catalog.mergeTargetLabel")}
          placeholder={t("pages.settings.catalog.mergeTargetPlaceholder")}
          data={options}
          value={targetId}
          onChange={setTargetId}
          searchable
          nothingFoundMessage={t("pages.settings.catalog.mergeTargetPlaceholder")}
        />
        {source && (
          <Alert color="yellow" title={t("pages.settings.catalog.mergeConfirmTitle")}>
            {t("pages.settings.catalog.mergeConfirmSystems", { count: source.systemCount })}
          </Alert>
        )}
        {error && (
          <Alert color="red" title={t("common.error")}>
            {error}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={mergeProducts.isPending}>
            {t("common.cancel")}
          </Button>
          <Button
            color="red"
            disabled={!targetId}
            loading={mergeProducts.isPending}
            onClick={() => void handleSubmit()}
          >
            {t("pages.settings.catalog.mergeConfirmAction")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * Neues Katalogprodukt anlegen.
 *
 * Bewusst schlank: Name, Anbieter, Versionsschema — die Vorlauf-Schwellen
 * setzt man danach in der Zeile, und ohne sie gilt die globale Schwelle. Ein
 * Formular, das alles auf einmal will, haelt beim haeufigsten Fall auf.
 */
function CreateProductModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const createProduct = useCreateProduct();
  const [name, setName] = useState("");
  const [vendor, setVendor] = useState("");

  useEffect(() => {
    if (opened) {
      setName("");
      setVendor("");
      createProduct.reset();
    }
    // `createProduct` ist bei jedem Rendern dieselbe Mutation-Instanz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  return (
    <Modal opened={opened} onClose={onClose} title={t("pages.settings.catalog.createTitle")}>
      <TextInput
        label={t("pages.settings.catalog.tableName")}
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        mb="sm"
        data-autofocus
      />
      <TextInput
        label={t("pages.settings.catalog.tableVendor")}
        value={vendor}
        onChange={(e) => setVendor(e.currentTarget.value)}
        mb="md"
      />
      {createProduct.isError && (
        <Text size="xs" c="red" mb="sm">
          {createProduct.error instanceof ApiError
            ? createProduct.error.message
            : t("maintenance.card.saveError")}
        </Text>
      )}
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          disabled={name.trim() === ""}
          loading={createProduct.isPending}
          onClick={() =>
            createProduct.mutate(
              {
                name: name.trim(),
                ...(vendor.trim() ? { vendor: vendor.trim() } : {}),
              },
              {
                onSuccess: (res) => {
                  notifications.show({
                    message: res.created
                      ? t("pages.settings.catalog.createdToast", { name: name.trim() })
                      : t("pages.settings.catalog.existedToast", { name: name.trim() }),
                    color: res.created ? "green" : "yellow",
                    autoClose: 4000,
                    withBorder: true,
                  });
                  onClose();
                },
              },
            )
          }
        >
          {t("common.save")}
        </Button>
      </Group>
    </Modal>
  );
}
